import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Backpack, ScrollText } from "lucide-react";
import { useWorld } from "../state/World";
import { fmtAxvTrim } from "../lib/tokens";
import EmailVerify from "./EmailVerify";

/**
 * The Pouch. One balance across both realms — this is the proof of one substrate.
 *
 * Closed by default, on purpose. You need navigation constantly and your balance
 * only when money is about to move; a permanently visible number trains the eye
 * to stop seeing it, which is fatal when the whole claim rests on someone
 * NOTICING it drop. So the bag opens on tap, and opens itself the moment a
 * transaction is in play or the balance actually changes.
 */
const SPEND_ROUTES = ["/playforward/play", "/stageward/table"];

export const PouchHUD = ({ bright }) => {
  const { me } = useWorld();
  const location = useLocation();
  const pouch = me?.pouch_sub ?? 0;
  const stake = me?.stake_sub ?? 0;
  // null until the first real balance lands. Seeding this with 0 made the
  // arrival of the true balance look like a credit — the HUD would announce a
  // phantom "+25.00" on every page load. The ledger must never be lied to.
  const prev = useRef(null);
  const [delta, setDelta] = useState(null);
  const [open, setOpen] = useState(false);

  const facingTransaction = SPEND_ROUTES.includes(location.pathname);

  useEffect(() => {
    if (!me) return;
    // Seeded lazily, on the first render where `me` actually exists. Seeding it
    // at declaration time meant the arrival of the true balance registered as a
    // credit and the HUD announced a phantom "+25.00" on every page load. Do not
    // move this initialisation back up to the useRef call.
    if (prev.current === null) {
      prev.current = pouch;
      return;
    }
    if (prev.current !== pouch) {
      const d = pouch - prev.current;
      prev.current = pouch;
      if (d !== 0) {
        setDelta(d);
        setOpen(true); // the balance moved — you are looking at it when it does
        const t = setTimeout(() => setDelta(null), 2600);
        return () => clearTimeout(t);
      }
    }
  }, [pouch, me]);

  // Standing at the till: the bag is already open before you are asked to agree.
  useEffect(() => {
    if (facingTransaction) setOpen(true);
  }, [facingTransaction]);

  if (!me) return null;
  const shut = () => !facingTransaction && setOpen(false);

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="pouch-toggle"
        aria-label={open ? "Close the Pouch" : "Open the Pouch"}
        title="The Pouch"
        onClick={() => (open ? shut() : setOpen(true))}
        className="glass relative flex h-10 items-center gap-1.5 rounded-full px-3.5 transition-transform duration-200 hover:scale-105"
      >
        <Backpack size={16} strokeWidth={1.6} className="text-[var(--stage-gold)]" />
        <span className="mono text-[9px] uppercase tracking-[0.18em] text-white/75">
          Pouch
        </span>
        <AnimatePresence>
          {delta !== null && !open && (
            <motion.span
              className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full"
              style={{ background: delta < 0 ? "var(--stage-ember)" : "#34d399" }}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
            />
          )}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="pouch-hud"
            className={`${bright ? "parchment" : "glass"} absolute bottom-full right-0 mb-3 w-[15rem] rounded-3xl px-5 py-4`}
            initial={{ opacity: 0, y: 10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.97 }}
            transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <div
              className={`tracked mb-3 text-[9px] ${bright ? "text-[#0f2530]/50" : "text-white/40"}`}
            >
              Pouch · Tokens (AXV)
            </div>

            <div className="relative">
              <div
                data-testid="pouch-balance"
                className="mono text-2xl leading-none"
                style={{ color: bright ? "#9a5a24" : "var(--stage-gold)" }}
              >
                {fmtAxvTrim(pouch)}
              </div>
              <AnimatePresence>
                {delta !== null && (
                  <motion.div
                    data-testid="pouch-delta"
                    className={`mono absolute left-0 top-full mt-1.5 whitespace-nowrap text-[11px] ${
                      delta < 0 ? "text-[var(--stage-ember)]" : "text-emerald-500"
                    }`}
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                  >
                    {delta < 0 ? "\u2212" : "+"}
                    {fmtAxvTrim(Math.abs(delta))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {stake > 0 && (
              <div className="mt-6" data-testid="stake-readout">
                <div
                  className={`tracked mb-1.5 text-[9px] ${bright ? "text-[#0f2530]/50" : "text-white/40"}`}
                >
                  Staked on yourself
                </div>
                <div
                  className="mono text-lg leading-none"
                  style={{ color: bright ? "#2c6e73" : "var(--arcade-cyan)" }}
                >
                  {fmtAxvTrim(stake)}
                </div>
              </div>
            )}

            <EmailVerify bright={bright} />

            <div
              className="mt-5 flex items-center justify-between pt-3"
              style={{
                borderTop: `1px solid ${bright ? "rgba(15,37,48,0.16)" : "rgba(255,255,255,0.1)"}`,
              }}
            >
              <span
                className={`mono text-[9px] ${bright ? "text-[#0f2530]/45" : "text-white/30"}`}
              >
                {me.user.display_name}
              </span>
              <Link
                to="/ledger"
                data-testid="hud-ledger-link"
                title="The Ledger"
                className={`tracked flex items-center gap-1.5 text-[9px] transition-colors ${
                  bright
                    ? "text-[#0f2530]/55 hover:text-[#9a5a24]"
                    : "text-white/40 hover:text-[var(--stage-gold)]"
                }`}
              >
                <ScrollText size={12} strokeWidth={1.5} /> Ledger
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PouchHUD;
